// src/components/BookingOverview.js
import React, { useState, useEffect, useCallback } from 'react';
// Frontend nutzt Backend-APIs; direkter Firestore-Zugriff entfällt für Aktionen
import * as seasonApi from '../services/seasonApiService';
import * as pitchApi from '../services/pitchApiService';
import * as bookingApi from '../services/bookingApiService';
import * as teamApi from '../services/teamApiService';
import { useAuth } from '../context/AuthProvider';
import { ReusableModal } from './Helpers/modalUtils';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery, Alert, Snackbar, CircularProgress, Divider, Checkbox, FormControlLabel, InputAdornment, Tooltip, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BlockIcon from '@mui/icons-material/Block';
import BuildIcon from '@mui/icons-material/Build';
import { StyledTableCell, filterData } from './Helpers/tableUtils';
import { formatDateForSearch } from './Helpers/dateUtils';

const BookingOverview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [awayTeam, setAwayTeam] = useState('');

  const [currentSeason, setCurrentSeason] = useState(null);
  const { currentUser, teamId } = useAuth();
  const [potentialOpponents, setPotentialOpponents] = useState([]);
  const [isOpponentLoading, setIsOpponentLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [submitting, setSubmitting] = useState(false);
  const [isFriendlyGame, setIsFriendlyGame] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFreeOnly, setShowFreeOnly] = useState(true);

  const inputStyle = {
    '& label.Mui-focused': { color: theme.palette.primary.main },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: theme.palette.divider },
      '&:hover fieldset': { borderColor: theme.palette.text.secondary },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
    },
    '& .MuiInputBase-input': { color: theme.palette.text.primary, accentColor: theme.palette.primary.main },
    '& label': { color: theme.palette.text.secondary },
    '& .MuiSelect-icon': { color: theme.palette.text.secondary },
    '& .Mui-disabled': { WebkitTextFillColor: `${theme.palette.text.disabled} !important`, color: `${theme.palette.text.disabled} !important` },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.action.disabledBackground },
  };

  // parseDate-Funktion wie im Admin
  // parseDate-Funktion wie im Admin
  const parseDate = (dateObj) => {
    if (!dateObj) return new Date();
    if (dateObj.toDate) return dateObj.toDate();
    if (dateObj._seconds) return new Date(dateObj._seconds * 1000);
    return new Date(dateObj);
  };

  const fetchData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      // Verwende öffentliche APIs für alle User (auch ohne Admin-Rechte)
      const activeSeason = await seasonApi.getActiveSeasonPublic().catch(() => null);
      if (!activeSeason) {
        console.error('Keine aktive Saison gefunden.');
        if (!isSilent) setLoading(false);
        return;
      }
      setCurrentSeason(activeSeason);

      const [bookingsData, pitchesData, teamsData] = await Promise.all([
        bookingApi.getPublicBookingsForSeason(activeSeason.id),
        pitchApi.getPublicPitches(),
        teamApi.getTeamsForActiveSeasonPublic()
      ]);

      // Formatiere die Daten wie im Admin
      const formattedBookings = bookingsData.map(b => ({ ...b, date: parseDate(b.date) }));

      // Konvertiere Teams-Array zu Objekt für einfacheren Zugriff
      const teams = teamsData.reduce((acc, t) => { acc[t.id] = t.name; return acc; }, {});

      setData({
        pitches: pitchesData,
        teams,
        bookings: formattedBookings
      });
      if (!isSilent) setLoading(false);
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      setNotification({ open: true, message: 'Fehler beim Laden der Daten.', severity: 'error' });
      if (!isSilent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBookNow = (booking) => {
    const bookingDate = new Date(booking.date);
    setSelectedSlot({
      id: booking.id,
      date: bookingDate,
      pitchId: booking.pitchId,
      time: bookingDate.toTimeString().slice(0, 5),
      friendly: booking.friendly || false
    });
    setIsFriendlyGame(booking.friendly || false);
  };

  // Lade potenzielle Gegner, sobald das Modal geöffnet wird
  useEffect(() => {
    const loadOpponents = async () => {
      if (!selectedSlot || !teamId) { setPotentialOpponents([]); return; }
      setIsOpponentLoading(true);
      try {
        const opponents = await teamApi.getPotentialOpponents(teamId, isFriendlyGame);
        setPotentialOpponents(opponents || []);
      } catch (e) {
        setPotentialOpponents([]);
      } finally {
        setIsOpponentLoading(false);
      }
    };
    loadOpponents();
  }, [selectedSlot, teamId, isFriendlyGame]);

  const submitBooking = async (e) => {
    e.preventDefault();
    // Nur eingeloggte Team-User dürfen buchen
    if (!currentUser || !teamId) {
      setNotification({ open: true, message: "Bitte melden Sie sich mit einem Team-Account an, um zu buchen.", severity: 'error' });
      return;
    }

    // Prüfe, ob aktuelle Saison vorhanden ist
    if (!currentSeason) {
      setNotification({ open: true, message: "Keine aktuelle Saison gefunden! Bitte kontaktieren Sie einen Administrator.", severity: 'error' });
      return;
    }

    if (!awayTeam) {
      setNotification({ open: true, message: "Bitte wählen Sie ein Auswärtsteam aus.", severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      // Anfrage an Backend: vorhandenen Slot anfragen
      await bookingApi.requestBookingSlot(
        selectedSlot.id,
        {
          homeTeamId: teamId,
          awayTeamId: awayTeam,
          friendly: isFriendlyGame
        }
      );

      setNotification({ open: true, message: "Buchung erfolgreich! Warte auf Bestätigung des Gegners.", severity: 'success' });
      setSelectedSlot(null);
      setAwayTeam('');

      // Refresh data
      await fetchData(true);
    } catch (error) {
      console.error("Fehler bei der Buchung:", error);
      setNotification({ open: true, message: error.message || "Buchung fehlgeschlagen.", severity: 'error' });
      // Refresh data to show current status without closing modal
      await fetchData(true);
    } finally {
      setSubmitting(false);
    }
  };

  const getPitchName = (pitchId) => {
    const pitch = data.pitches?.find(p => p.id === pitchId);
    return pitch ? pitch.name : 'Unbekannter Platz';
  };

  const getTeamName = (teamId) => {
    if (!teamId) return null;
    const team = data.teams?.[teamId];
    return team || 'Unbekannt';
  };
  const displayTeamName = (teamId) => getTeamName(teamId) || '-';

  // Helper to check league limit
  const hasReachedLeagueLimit = () => {
    if (!currentSeason || !teamId || !data.bookings || !data.teams) return false;

    const totalTeams = Object.keys(data.teams).length;
    if (totalTeams < 2) return false;

    const maxGamesPerOpponent = currentSeason.playMode === 'double_round_robin' ? 2 : 1;
    const maxLeagueGames = (totalTeams - 1) * maxGamesPerOpponent;

    const myLeagueGames = data.bookings.filter(b =>
      (b.homeTeamId === teamId || b.awayTeamId === teamId) &&
      ['confirmed', 'pending_home_confirm', 'pending_away_confirm'].includes(b.status) &&
      !b.friendly
    ).length;

    return myLeagueGames >= maxLeagueGames;
  };

  const leagueLimitReached = hasReachedLeagueLimit();

  // Helper for status labels and colors
  const getStatusConfig = (status, isAvailable) => {
    // ... existing implementation ...
    if (isAvailable) return { label: 'Frei', color: theme.palette.success.main, icon: <EventAvailableIcon />, iconColor: 'success' };

    switch (status) {
      case 'booked':
      case 'confirmed':
        return { label: 'Belegt', color: theme.palette.error.main, icon: <EventBusyIcon />, iconColor: 'error' };
      case 'pending_home_confirm':
      case 'pending_away_confirm':
        return { label: 'Bestätigung ausstehend', color: theme.palette.warning.main, icon: <EventBusyIcon />, iconColor: 'warning' };
      case 'blocked':
        return { label: 'Gesperrt', color: theme.palette.grey[500], icon: <BlockIcon />, iconColor: 'default' };
      case 'maintenance':
        return { label: 'Wartung', color: theme.palette.warning.main, icon: <BuildIcon />, iconColor: 'warning' };
      default:
        return { label: 'Belegt', color: theme.palette.grey[500], icon: <EventBusyIcon />, iconColor: 'default' };
    }
  };

  // ... existing filtering logic ...
  // Nur zukünftige Buchungen anzeigen (Vergangenheit ausblenden)
  // UND nur Buchungen auf offiziellen Plätzen anzeigen
  const now = new Date();
  const upcomingBookings = (data.bookings || []).filter((booking) => {
    try {
      const bookingDate = new Date(booking.date);
      // Check if pitch is in the loaded (official) pitches list AND is verified
      const isOfficialPitch = data.pitches?.some(p => p.id === booking.pitchId && p.isVerified);

      const isAvailable = booking.status === 'available' || (booking.isAvailable === true && !booking.homeTeamId && !booking.awayTeamId);

      if (showFreeOnly && !isAvailable) return false;

      return bookingDate >= now && isOfficialPitch;
    } catch {
      return false;
    }
  });

  const searchableFields = [
    { key: 'date', accessor: (item) => formatDateForSearch(item.date) },
    { key: 'pitchId', accessor: (item) => getPitchName(item.pitchId) },
    { key: 'homeTeamId', accessor: (item) => getTeamName(item.homeTeamId) },
    { key: 'awayTeamId', accessor: (item) => getTeamName(item.awayTeamId) },
    { key: 'status', accessor: (item) => item.status === 'available' ? 'Frei' : (item.status === 'blocked' ? 'Gesperrt' : 'Belegt') }
  ];

  const filteredBookings = filterData(upcomingBookings, searchTerm, searchableFields);

  const bookingsByPitch = filteredBookings.reduce((acc, booking) => {
    const pitchId = booking.pitchId;
    if (!acc[pitchId]) {
      acc[pitchId] = [];
    }
    acc[pitchId].push(booking);
    return acc;
  }, {});

  Object.keys(bookingsByPitch).forEach(pitchId => {
    bookingsByPitch[pitchId].sort((a, b) => new Date(a.date) - new Date(b.date));
  });

  const sortedPitchIds = Object.keys(bookingsByPitch).sort((a, b) => {
    const pitchA = getPitchName(a);
    const pitchB = getPitchName(b);
    return pitchA.localeCompare(pitchB);
  });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: theme.palette.primary.main }} /></Box>;
  }

  // Lookup current booking status for the modal
  const currentModalBooking = selectedSlot ? (data.bookings || []).find(b => b.id === selectedSlot.id) : null;
  const isModalBookingAvailable = currentModalBooking ? (
    currentModalBooking.status === 'available' ||
    (currentModalBooking.isAvailable === true && !currentModalBooking.homeTeamId && !currentModalBooking.awayTeamId)
  ) : false;

  const modalDisabled = !isModalBookingAvailable || submitting;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }}>{notification.message}</Alert>
      </Snackbar>

      <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ mb: 2, mt: 2, color: theme.palette.primary.main, fontWeight: 700, fontFamily: 'Comfortaa', textAlign: 'center', textTransform: 'uppercase' }}>
        Spielplan & Reservierung
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          name="booking-search"
          placeholder="Suche nach Datum, Platz, Team..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoComplete="off"
          sx={{
            ...inputStyle,
            maxWidth: '600px'
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showFreeOnly}
              onChange={(e) => setShowFreeOnly(e.target.checked)}
              sx={{
                color: theme.palette.primary.main,
                '&.Mui-checked': {
                  color: theme.palette.primary.main,
                },
              }}
            />
          }
          label={<Typography sx={{ fontFamily: 'Comfortaa', color: theme.palette.text.primary, fontSize: '0.8rem' }}>Freie Slots</Typography>}
        />
      </Box>

      {sortedPitchIds.length === 0 ? (
        <Paper sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, p: { xs: 3, sm: 5 }, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ color: theme.palette.text.secondary, fontFamily: 'Comfortaa' }}>
            {searchTerm ? 'Keine passenden Buchungen gefunden.' : 'Keine Zeitslots für die aktuelle Saison vorhanden.'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sortedPitchIds.map(pitchId => {
            const pitchBookings = bookingsByPitch[pitchId];
            const pitchName = getPitchName(pitchId);

            return (
              <Box key={pitchId} sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
                <Accordion defaultExpanded={!!searchTerm} key={pitchId + (searchTerm ? '-expanded' : '')} sx={{ width: '100%' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 700, fontFamily: 'comfortaa', textTransform: 'uppercase' }}>
                      {pitchName}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer component={Box} sx={{ backgroundColor: theme.palette.background.paper }}>
                      <Table size="small">
                        <TableHead>
                          {isMobile ? (
                            <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                              <TableCell align="center" colSpan={7} sx={{ color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Verfügbare Termine</TableCell>
                            </TableRow>
                          ) : (
                            <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                              <TableCell align="center" sx={{ width: '40px', color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Status</TableCell>
                              <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Datum</TableCell>
                              <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Zeitraum</TableCell>
                              <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Heim</TableCell>
                              <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Auswärts</TableCell>
                              <TableCell align="center" sx={{ color: theme.palette.text.primary, fontWeight: 'bold', borderBottom: `1px solid ${theme.palette.divider}` }}>Aktion</TableCell>
                            </TableRow>
                          )}
                        </TableHead>
                        <TableBody>
                          {pitchBookings.map(booking => {
                            const startTime = new Date(booking.date).toTimeString().slice(0, 5);
                            const endTime = booking.duration ? new Date(new Date(booking.date).getTime() + booking.duration * 60000).toTimeString().slice(0, 5) : '-';
                            const timeRange = `${startTime} - ${endTime}`;

                            const isAvailable = booking.status === 'available' ||
                              (booking.isAvailable === true && !booking.homeTeamId && !booking.awayTeamId);

                            const statusConfig = getStatusConfig(booking.status, isAvailable);

                            let isBookable = isAvailable;

                            if (isAvailable && leagueLimitReached && !booking.friendly) {
                              isBookable = false;
                            }

                            return isMobile ? (
                              <TableRow
                                key={booking.id}
                                onClick={() => isBookable && handleBookNow(booking)}
                                sx={{
                                  backgroundColor: theme.palette.background.default,
                                  cursor: isBookable ? 'pointer' : 'default',
                                  '&:hover': { backgroundColor: isBookable ? theme.palette.action.hover : 'inherit' }
                                }}
                              >
                                <TableCell colSpan={7} sx={{ p: 0, border: 'none', borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: '70px' }}>
                                    <Box sx={{ width: '4px', bgcolor: statusConfig.color }} />
                                    <Box sx={{ flexGrow: 1, p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ textAlign: 'center', pr: 2 }}>
                                          <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>{new Date(booking.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: theme.palette.text.primary }}>{timeRange}</Typography>
                                            <Typography sx={{ color: booking.friendly ? '#FFD700' : 'transparent', fontWeight: 'bold', fontSize: '0.8rem', userSelect: 'none' }}>F</Typography>
                                          </Box>
                                        </Box>
                                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', pl: 2, borderLeft: `1px solid ${theme.palette.divider}` }}>
                                          {isAvailable ? (
                                            <Typography sx={{ fontSize: '0.8rem', color: theme.palette.success.main }}>Frei</Typography>
                                          ) : (
                                            <>
                                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: theme.palette.text.primary, textAlign: 'center' }}>{displayTeamName(booking.homeTeamId)}</Typography>
                                              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', my: 0.25 }}>vs.</Typography>
                                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: theme.palette.text.primary, textAlign: 'center' }}>{displayTeamName(booking.awayTeamId)}</Typography>
                                            </>
                                          )}
                                        </Box>
                                        <Box sx={{ pl: 2, borderLeft: `1px solid ${theme.palette.divider}` }}>
                                          {isBookable ? (
                                            <Tooltip title="Platz buchen">
                                              <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleBookNow(booking); }}>
                                                <EventAvailableIcon />
                                              </IconButton>
                                            </Tooltip>
                                          ) : (
                                            <Tooltip title={statusConfig.label}>
                                              <span>
                                                <IconButton size="small" color={statusConfig.iconColor} disabled>
                                                  {statusConfig.icon}
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ) : (
                              <TableRow
                                key={booking.id}
                                onClick={() => isBookable && handleBookNow(booking)}
                                sx={{
                                  cursor: isBookable ? 'pointer' : 'default',
                                  '&:hover': { backgroundColor: isBookable ? theme.palette.action.hover : 'inherit' }
                                }}
                              >
                                <StyledTableCell align="center">
                                  <Box sx={{ width: '10px', height: '10px', bgcolor: statusConfig.color, borderRadius: '50%', boxShadow: `0 0 8px ${statusConfig.color}` }} title={statusConfig.label} />
                                </StyledTableCell>
                                <StyledTableCell>{new Date(booking.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}</StyledTableCell>
                                <StyledTableCell>
                                  {timeRange}
                                  <Typography component="span" sx={{ ml: 1, color: booking.friendly ? '#FFD700' : 'transparent', fontWeight: 'bold', userSelect: 'none' }}>F</Typography>
                                </StyledTableCell>
                                <StyledTableCell>{isAvailable ? '-' : displayTeamName(booking.homeTeamId)}</StyledTableCell>
                                <StyledTableCell>{isAvailable ? '-' : displayTeamName(booking.awayTeamId)}</StyledTableCell>
                                <StyledTableCell align="center">
                                  {isBookable ? (
                                    <Tooltip title="Platz buchen">
                                      <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleBookNow(booking); }}>
                                        <EventAvailableIcon />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title={statusConfig.label}>
                                      <span>
                                        <IconButton size="small" color={statusConfig.iconColor === 'default' ? 'inherit' : statusConfig.iconColor} disabled>
                                          {statusConfig.icon}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </StyledTableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Pop-up-Formular für die Buchung */}
      {selectedSlot && (
        <ReusableModal open={!!selectedSlot} onClose={() => setSelectedSlot(null)} title="Platz buchen">
          <Box component="form" onSubmit={submitBooking} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!isModalBookingAvailable && (
              <Alert severity="error" sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', color: theme.palette.error.main }}>
                {(() => {
                  const status = currentModalBooking?.status;
                  if (status === 'blocked') return 'Dieser Termin wurde gerade gesperrt (Wochenlimit erreicht).';
                  if (status === 'booked' || status === 'confirmed' || status === 'pending_away_confirm' || status === 'pending_home_confirm') return 'Dieser Termin wurde gerade von einem anderen Team reserviert.';
                  return 'Dieser Termin ist nicht mehr verfügbar.';
                })()}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField size="small" label="Datum" type="date" fullWidth value={new Date(selectedSlot.date).toISOString().split('T')[0]} InputLabelProps={{ shrink: true }} sx={inputStyle} disabled />
              <TextField size="small" label="Uhrzeit" type="time" fullWidth value={selectedSlot.time} InputLabelProps={{ shrink: true }} sx={inputStyle} disabled />
            </Box>
            <TextField size="small" label="Dauer (Minuten)" type="number" fullWidth value={(data.bookings || []).find(b => b.id === selectedSlot.id)?.duration || 90} sx={inputStyle} disabled />
            <FormControl size="small" fullWidth sx={inputStyle} disabled>
              <InputLabel>Platz</InputLabel>
              <Select value={selectedSlot.pitchId} label="Platz" MenuProps={{ PaperProps: { sx: { bgcolor: theme.palette.background.paper, color: theme.palette.text.primary } } }} sx={{ color: theme.palette.text.primary }}>
                {data.pitches.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />

            <Box>
              <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'bold', mb: 0.5 }}>Heim-Mannschaft</Typography>
              <Typography sx={{ color: theme.palette.text.secondary }}>{data.teams[teamId] || 'Dein Team'}</Typography>
            </Box>

            <Box>
              <Typography sx={{ color: theme.palette.text.primary, fontWeight: 'bold', mb: 0.5 }}>Auswärts-Mannschaft</Typography>
              <FormControl fullWidth size="small" sx={{ borderColor: theme.palette.divider, bgcolor: theme.palette.background.paper }}>
                <Select sx={{ color: theme.palette.text.primary }}
                  label="Auswärts-Mannschaft"
                  value={awayTeam}
                  onChange={(e) => setAwayTeam(e.target.value)}
                  required
                  MenuProps={{ PaperProps: { sx: { bgcolor: theme.palette.background.paper, color: theme.palette.text.primary } } }}
                  disabled={isOpponentLoading || modalDisabled}
                >
                  <MenuItem value=""><em>-</em></MenuItem>
                  {isOpponentLoading ? (
                    <MenuItem disabled><em>Lade Gegner...</em></MenuItem>
                  ) : (
                    potentialOpponents.map(opponent => (
                      <MenuItem key={opponent.id} value={opponent.id}>
                        {opponent.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel
              control={<Checkbox
                checked={isFriendlyGame}
                onChange={(e) => setIsFriendlyGame(e.target.checked)}
                sx={{ color: 'grey.100', '&.Mui-checked': { color: '#FFD700' }, '&.Mui-disabled': { color: 'grey.700' } }}
                disabled={!selectedSlot.friendly || modalDisabled}
              />}
              label={
                <Box>
                  <Typography sx={{ color: (!selectedSlot.friendly || modalDisabled) ? 'grey.600' : 'grey.100' }}>Freundschaftsspiel</Typography>
                  {!selectedSlot.friendly && <Typography variant="caption" sx={{ color: 'grey.600' }}>Nicht freigegeben</Typography>}
                </Box>
              }
            />

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
              <Button variant="outlined" color="inherit" onClick={() => setSelectedSlot(null)} sx={{ color: theme.palette.text.secondary, borderColor: theme.palette.divider }}>Abbrechen</Button>
              <Button variant="contained" sx={{ bgcolor: theme.palette.primary.main }} type="submit" disabled={!awayTeam || modalDisabled}>
                {submitting ? <CircularProgress size={20} /> : 'Jetzt buchen'}
              </Button>
            </Box>
          </Box>
        </ReusableModal>
      )}
    </Box>
  );
};

export default BookingOverview;